import React, { useEffect, useState, useRef, useCallback } from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet, ActivityIndicator, FlatList } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@cubemeet/core/services/supabase';
import { getTravelBackgroundAssetNoFallback } from '@cubemeet/ui';
import { MdVisibility, MdFavorite, MdInsertComment, MdLocationOn } from 'react-icons/md';

interface GlobalPost {
  id: string;
  author_id: string;
  title: string;
  summary?: string;
  cover_image?: string;
  city?: string;
  tags?: string[];
  view_count: number;
  like_count: number;
  comment_count: number;
  created_at: string;
}

interface GlobalPostsProps {}

/**
 * GlobalPosts - 展示全网公开游记
 */
const GlobalPosts: React.FC<GlobalPostsProps> = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<GlobalPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // Ref for the sentinel element to trigger lazy loading
  const observerTarget = useRef<View>(null);

  const loadPosts = useCallback(async (pageNum: number) => {
    try {
      setLoading(true);
      const pageSize = 10;
      const start = (pageNum - 1) * pageSize;

      // 获取公开游记 - 注意条件：status='published'
      const { data, error, count } = await supabase
        .from('travel_posts')
        .select('*', { count: 'exact' })
        .eq('status', 'published')
        .order('created_at', { ascending: false })
        .range(start, start + pageSize - 1);

      if (error) {
        console.error('Failed to load global posts:', error);
        return;
      }

      setTotalCount(count || 0);
      if (pageNum === 1) {
        setPosts(data || []);
      } else {
        setPosts((prev) => [...prev, ...(data || [])]);
      }

      setHasMore(start + pageSize < (count || 0));
      setPage(pageNum);
    } catch (error) {
      console.error('加载全网游记失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts(1);
  }, [loadPosts]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const currentTarget = observerTarget.current;
    // Cast to any to access the DOM node from the React Native Web View ref if needed, 
    // but usually ref on View in RNW gives the DOM node or a wrapper.
    // In React Native Web, refs on View might return the component instance.
    // We need the DOM node for IntersectionObserver.
    // However, standard IntersectionObserver works with Element.
    // Let's try to observe the ref directly. If it fails, we might need `findNodeHandle` or similar, 
    // but in modern RNW refs usually forward to DOM node or have setNativeProps.
    // Actually, for RNW, it's safer to use a callback ref or assume standard behavior if it's a div.
    
    // Note: In React Native Web, View renders a div.
    // But the ref might be the View component instance.
    // Let's check if we can get the DOM node.
    
    if (!currentTarget) return;

    // Safe check for DOM node access in RNW environment
    const domNode = (currentTarget as any).setNativeProps ? (currentTarget as any) : currentTarget; 
    // Actually, let's just try observing the currentTarget. 
    // If it's a React Component, this will fail.
    // But `useRef<View>(null)` implies it might be the component.
    // Let's use a callback ref to be sure, or just try.
    
    // A safer way in RNW is often just using the ref if it's a host component.
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadPosts(page + 1);
        }
      },
      { threshold: 0.1 }
    );

    // In many RNW versions, the ref is the DOM node.
    if (domNode instanceof Element) {
        observer.observe(domNode);
    } else {
        // Fallback or retry? 
        // If we can't get the DOM node, lazy load won't work.
        // Let's assume standard RNW behavior where ref is the node or close to it.
        // If `domNode` is not an Element, we might need `findNodeHandle` but that's from 'react-native'.
        // Since this is a web project (apps/web), we can probably assume standard React DOM behavior 
        // if we were using <div>, but we are using <View>.
        // Let's try observing `currentTarget` directly if it looks like an Element.
         if (currentTarget instanceof Element) {
             observer.observe(currentTarget);
         }
    }

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loading, page, loadPosts]);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadPosts(page + 1);
    }
  };

  if (loading && posts.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color="#ff6b35" />
        <Text style={styles.loadingText}>加载游记中...</Text>
      </View>
    );
  }

  if (posts.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>暂无公开游记</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {posts.map((post, idx) => {
        // 解析封面圖：優先作為本地資源處理，否則作為 URL
        const resolveCoverSource = () => {
          if (!post.cover_image) return undefined;
          
          // 先嘗試作為本地資源解析（如 assets/images/xxx.webp）
          const assetSource = getTravelBackgroundAssetNoFallback(post.cover_image);
          if (assetSource) return assetSource;
          
          // 否則作為 URL 處理
          return { uri: post.cover_image };
        };

        const coverSource = resolveCoverSource();

        return (
          <TouchableOpacity
            key={post.id}
            style={[styles.postCard, idx === posts.length - 1 ? styles.postCardLast : undefined]}
            onPress={() => navigate(`/travel-post/${post.id}`)}
            activeOpacity={0.7}
          >
            {/* 封面图 */}
            {coverSource && (
              <Image source={coverSource} style={styles.coverImage} />
            )}

          {/* 内容 */}
          <View style={styles.postContent}>
            <Text style={styles.postTitle} numberOfLines={2}>
              {post.title}
            </Text>

            {post.summary && (
              <Text style={styles.postSummary} numberOfLines={2}>
                {post.summary}
              </Text>
            )}

            {/* 元数据 */}
            <View style={styles.postMeta}>
              <View style={styles.statsRow}>
                <View style={styles.statItem}>
                  <MdVisibility size={14} color="#999" />
                  <Text style={styles.statText}>{post.view_count}</Text>
                </View>
                <Text style={styles.statDivider}>·</Text>
                <View style={styles.statItem}>
                  <MdFavorite size={14} color="#999" />
                  <Text style={styles.statText}>{post.like_count}</Text>
                </View>
                <Text style={styles.statDivider}>·</Text>
                <View style={styles.statItem}>
                  <MdInsertComment size={14} color="#999" />
                  <Text style={styles.statText}>{post.comment_count}</Text>
                </View>
              </View>
              
              {post.city && (
                <View style={styles.locationRow}>
                  <MdLocationOn size={14} color="#0ea5a4" />
                  <Text style={styles.cityText}>{post.city}</Text>
                </View>
              )}
            </View>
          </View>
        </TouchableOpacity>
        );
      })}

      {/* 加载更多 (Lazy Load Sentinel) */}
      {hasMore && (
        <View ref={observerTarget} style={styles.loadMoreButton}>
          {loading && <ActivityIndicator size="small" color="#0ea5a4" style={{ marginRight: 8 }} />}
          <Text style={styles.loadMoreText}>
            {loading ? '正在加载更多...' : '加载更多游记'}
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: 0,
  },

  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
    gap: 8,
  },
  loadingText: {
    fontSize: 13,
    color: '#666',
  },

  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyText: {
    fontSize: 14,
    color: '#ccc',
  },

  postCard: {
    marginBottom: 4,
    backgroundColor: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#f0f0f0',
  },

  // 最后一条卡片使用更小的底部间距，避免与底部导航出现过大空白
  postCardLast: {
    marginBottom: 0,
  },

  coverImage: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#f0f0f0',
  },

  postContent: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },

  postTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },

  postSummary: {
    fontSize: 12,
    color: '#666',
    lineHeight: 18,
    marginBottom: 8,
  },

  postMeta: {
    gap: 6,
  },

  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },

  statText: {
    fontSize: 11,
    color: '#999',
  },

  statDivider: {
    fontSize: 11,
    color: '#ccc',
    marginHorizontal: 6,
  },

  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },

  cityText: {
    fontSize: 11,
    color: '#0ea5a4',
    fontWeight: '500',
  },

  loadMoreButton: {
    paddingVertical: 12,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },

  loadMoreText: {
    fontSize: 13,
    color: '#0ea5a4',
    fontWeight: '500',
  },
});

export default GlobalPosts;
